import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Notification, ThemeProvider } from '@workboard/wobo-ui';
import {
  TEAM_SUMMARY_APIS,
  mapToConnectSummaryDTO,
  normalizeTeamSummaryWireToYiiFormat,
  parseOptionalPositiveInt,
} from './coauthor-team-summary.constants';

const id = 'coauthor-summary-component';

/**
 * Provides a mount point for the Co-Author React app
 * and mounts it when this component mounts.
 */
export function CoauthorTeamSummaryComponent(props: any) {
  const { t } = useTranslation();
  // Notification
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    if (Boolean(window.wbConfig?.teamSummaryGenAiEnabled) == false) {
      return null;
    }
    getCoauthorSummaryProps(props).then((coAuthorSummaryProps) => {
      console.log('coAuthorSummaryProps', coAuthorSummaryProps);
      window.initCoauthorSummaryComponent?.(id, coAuthorSummaryProps);
    });
  }, []);

  const getTeamSummary = async (params: any): Promise<any> => {
    const response = await axios.get(TEAM_SUMMARY_APIS.GET_SUMMARY, {
      params: { teamId: params.resourceId },
    });
    console.log('response', response.data);
    return mapToConnectSummaryDTO(response.data.summary);
  };

  const generateSummary = async (data: any): Promise<any> => {
    const headers = { 'YII-CSRF-TOKEN': window.csrfToken };
    const normalized = normalizeTeamSummaryWireToYiiFormat(
      data.format,
      data.genAiBriefCustomId,
    );
    // Any org template id implies Yii format 4. UI may still send 1–3 (e.g. "paragraph" as 2).
    const dataGid = parseOptionalPositiveInt(data.genAiBriefCustomId);
    let { formatChoice } = normalized;
    if (dataGid != null) {
      formatChoice = 4;
    }
    const genAiBriefCustomId =
      formatChoice === 4
        ? normalized.genAiBriefCustomId ?? dataGid
        : null;
    const body = {
      teamId: data.resourceId,
      isPublished: false,
      focus: data.focus,
      timePeriod: data.timePeriod,
      format: formatChoice,
      customPrompt: formatChoice === 4 ? data.customPrompt || null : null,
      language: data.language,
      ...(genAiBriefCustomId != null ? { genAiBriefCustomId } : {}),
    };

    let response;
    try {
      response = await axios.post(TEAM_SUMMARY_APIS.GENERATE_SUMMARY, body, {
        headers,
      });
    } catch (error) {
      // ui-summary's error toast shows the rejection's message, so carry the server's reason
      const failure = error as { response?: { data?: { message?: string } } };
      const reason = failure?.response?.data?.message;
      throw reason ? new Error(reason) : error;
    }

    return mapToConnectSummaryDTO(response.data.summary);
  };

  const publishSummary = async (id: number, data: any): Promise<any> => {
    const headers = { 'YII-CSRF-TOKEN': window.csrfToken };
    const normalized = normalizeTeamSummaryWireToYiiFormat(
      data.format,
      data.genAiBriefCustomId,
    );
    const dataGid = parseOptionalPositiveInt(data.genAiBriefCustomId);
    let { formatChoice } = normalized;
    if (dataGid != null) {
      formatChoice = 4;
    }
    const genAiBriefCustomId =
      formatChoice === 4
        ? normalized.genAiBriefCustomId ?? dataGid
        : null;
    const payload = {
      ...data,
      teamId: data.resourceId,
      isPublished: true,
    };
    if (data.format !== undefined && data.format !== null) {
      payload.format = formatChoice;
      if (formatChoice === 4 && genAiBriefCustomId != null) {
        payload.genAiBriefCustomId = genAiBriefCustomId;
      } else {
        delete payload.genAiBriefCustomId;
      }
    }
    // Mirror generate: org prompt is server-resolved; body customPrompt is often null.
    payload.customPrompt = formatChoice === 4 ? data.customPrompt || null : null;
    // delete resource id and type to avoid confusing the backend
    delete payload.resourceId;
    delete payload.resourceType;

    const response = await axios.put(TEAM_SUMMARY_APIS.PUBLISH_SUMMARY, payload, {
      headers,
    });

    return mapToConnectSummaryDTO(response.data.summary);
  };

  const getCoauthorSummaryProps = async (props): Promise<any> => {
    // need to check readonly;
    const { teamId } = props;
    const response = await axios.get(TEAM_SUMMARY_APIS.GET_ACCESS_LEVEL, { params: { teamId } });

    const canEdit = response?.data?.isManagerOrCoManagerOrAdmin ? true : false;
    return {
      id: props.teamId,
      type: 'team',
      title: 'Team Brief',
      onGetSummary: getTeamSummary,
      onGenerateSummary: generateSummary,
      onPublishSummary: publishSummary,
      isReadonly: !canEdit,
      //   customFilters: [],
      //   hideFocusFilter: false,
      //   hideFormatFilter: false,
      //   hideTimePeriodFilter: false,
      //   hideCopyIcon: false,
      //   hideGenerateIcon: false,
      //   hidePublishIcon: false,
      //   hideShareMsTeamIcon: false,
      hideInsertIcon: true,
      //   hideLanguageFilter: true,
      //   onInsertClick: (s: any) => console.log('summary', s),
      onToast: (message) => setToastMessage(message),
    };
  };
  if (Boolean(window.wbConfig?.teamSummaryGenAiEnabled) == false) {
    return null;
  }
  return (
    <ThemeProvider>
      <div id={id} style={{ padding: '10px 20px' }}></div>
      <Notification
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        message={t(toastMessage?.message)}
        onClose={() => setToastMessage(null)}
        severity={toastMessage?.toastType}
        persist={false}
        open={toastMessage !== null}
      ></Notification>
    </ThemeProvider>
  );
}
